import {
  DeleteOutlined,
  LineChartOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { AccordionItem, Input, Tooltip } from '@mm/components';
import { useSignalSourceConfigurationsCRUDActions } from '@mm/state/actions/SignalSourceConfigurationsCRUDActions';
import { useSignalVisitedUrlEventTriggersCRUDActions } from '@mm/state/actions/SignalVisitedUrlEventTriggersCRUDActions';
import { UpdateEventTrigger } from '@mm/state/controllers/SignalVisitedUrlEventTriggersController';
import { KlaviyoEvents } from '@mm/state/model/KlaviyoModel';
import {
  Configuration,
  VisitedUrlEventTrigger,
} from '@mm/state/model/SignalModel';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectSignalConfiguration } from '@mm/state/selectors/signalSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Select, Switch } from 'antd';
import { ReactComponent as CustomizeIcon } from 'assets/customize.svg';
import classNames from 'classnames';
import { debounce, difference } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useConnectKlaviyo } from '../../Integrations/NativeIntegrations/components/KlaviyoIntegrations/use-connect-klaviyo';
import '../../Integrations/NativeIntegrations/IntegrationBaseComponent.scss';
import styles from './ManualSignal.module.css';

const possibleMetrics = [
  'Website visit',
  'Collection view',
  'Product view',
  'Added to Cart',
  'Checkout Started',
  'Cart abandonment',
  'Checkout abandonment',
  'Return visit',
];

export const legacyMetrics = ['Cart abandonment', 'Checkout abandonment'];

export const klaviyoEventsCamelizedMetricsMap = {
  'Website visit': 'websiteVisit',
  'Collection view': 'collectionView',
  'Product view': 'productView',
  'Added to Cart': 'addedToCart',
  'Checkout Started': 'checkoutStarted',
  'Cart abandonment': 'cartAbandonment',
  'Checkout abandonment': 'checkoutAbandonment',
  'Return visit': 'returnVisit',
};

type EventTriggerProps = {
  configurationId: number;
  trigger: VisitedUrlEventTrigger;
  metrics: string[];
  update: (configurationId: number, trigger: UpdateEventTrigger) => void;
  remove: (configurationId: number, id: number) => void;
  eventsStats: KlaviyoEvents;
};

export const EventTrigger = ({
  update,
  remove,
  trigger,
  metrics,
  configurationId,
  eventsStats,
}: EventTriggerProps) => {
  const [pattern, setPattern] = useState(trigger.pattern || undefined);
  const eventsSent =
    eventsStats[klaviyoEventsCamelizedMetricsMap[trigger.metric]] || 0;
  const debouncedUpdate = useRef(
    debounce(
      async (pattern: string, trigger: VisitedUrlEventTrigger) =>
        update(configurationId, {
          id: trigger.id,
          pattern,
        }),
      1000,
    ),
  ).current;
  return (
    <>
      <div className={styles.gridColumn}>
        <Select
          className={styles.eventsSelect}
          placeholder="Choose"
          defaultValue={trigger.metric || undefined}
          onChange={(metric: string) =>
            update(configurationId, {
              id: trigger.id,
              metric,
            })
          }
          options={metrics.map(metric => ({
            value: metric,
            label: metric,
          }))}
          popupClassName={styles.popup}
        />
      </div>
      <div className={styles.gridColumn}>
        {trigger.metric === 'Return visit' ? (
          <Input
            css={{
              container: styles.urlPatternContainer,
              input: styles.input,
            }}
            type="text"
            disabled={true}
            value="This event is triggered on returning visits"
          />
        ) : (
          <Input
            css={{
              container: styles.urlPatternContainer,
              input: styles.input,
            }}
            placeholder="Add URL keyword ex: /Cart"
            value={pattern}
            onChange={e => {
              const { value } = e.target;
              setPattern(value);
              debouncedUpdate(value, trigger);
            }}
          />
        )}
      </div>
      <div className={classNames(styles.gridColumn, styles.alignedColumn)}>
        <Switch
          defaultChecked={trigger.allContacts}
          checkedChildren="All"
          unCheckedChildren="Synced"
          className={styles.switch}
          onChange={ok =>
            update(configurationId, { allContacts: ok, id: trigger.id })
          }
        />
      </div>
      <div className={classNames(styles.gridColumn, styles.alignedColumn)}>
        <LineChartOutlined /> {Intl.NumberFormat().format(eventsSent)}
      </div>
      <div className={classNames(styles.gridColumn, styles.alignedColumn)}>
        <DeleteOutlined onClick={() => remove(configurationId, trigger.id)} />
      </div>
    </>
  );
};

export const ManualSignal = ({
  isManualSignalActive,
  handleManualSignalToggle,
}: {
  isManualSignalActive: boolean;
  handleManualSignalToggle: (
    checked: boolean,
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => void;
}) => {
  const configuration = useSelector(selectSignalConfiguration);

  const { getEvents } = useConnectKlaviyo();
  const hasLegacyMetrics = useSelector(
    companyFeatureFlagsSelector,
  ).legacy_signal_metrics;

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);

  const eventTriggers = useSelector(
    (state: StoreState) =>
      state.signalVisitedUrlEventTriggers.visitedUrlEventTriggers,
  );

  const allowedMetrics = hasLegacyMetrics
    ? possibleMetrics
    : difference(possibleMetrics, legacyMetrics);

  const availableMetrics = allowedMetrics.filter(
    metric => !eventTriggers.find(trigger => trigger.metric === metric),
  );

  const eventsStats = useSelector((state: StoreState) => state.klaviyo.events);

  const configurationId = (configuration as Configuration)?.id;

  const canAdd = eventTriggers.length < possibleMetrics.length;

  const { create, update, remove, retrieveAll } =
    useSignalVisitedUrlEventTriggersCRUDActions();

  const { retrieveAll: retrieveSourceConfigurations } =
    useSignalSourceConfigurationsCRUDActions();

  useEffect(() => {
    if (configurationId) {
      retrieveSourceConfigurations(configurationId);
      retrieveAll(configurationId);
    }
  }, [configurationId]);

  useEffect(() => {
    getEvents(activeFbPageId, false);
  }, [activeFbPageId]);

  return (
    <AccordionItem
      className={classNames('integrations-item', styles.container)}
      closeOnOutsideClick={false}
      openOnMount={false}
      showArrow={true}
      caretStyleIcon
      icon={
        <Tooltip
          title="When you activate Manual Link Tracking Signal, Shopify Signal will be
          stopped automatically. Only one can work at time."
          placement="bottom"
        >
          <div className={styles.switchContainer}>
            <Switch
              checked={isManualSignalActive}
              onChange={handleManualSignalToggle}
            />
          </div>
        </Tooltip>
      }
      titleComponent={
        <div className={styles.titleContainer}>
          <div className={styles.customizeIcon}>
            <CustomizeIcon />
          </div>
          <div className={styles.titleInfo}>
            <div>
              <span className={styles.title}>Manual Link Tracking Signal </span>
            </div>
          </div>
          <div className={styles.titleButtonContainer}>
            {/* Note: coming up in the future 
             <PrimaryButton
              color="secondary-blue"
              className={styles.titleButton}
              disabled={!isManualSignalActive}
            >
              Set up Conversions
            </PrimaryButton> */}
          </div>
        </div>
      }
    >
      <div className={styles.whiteBlock}>
        {eventTriggers.length > 0 ? (
          <div className={styles.eventTriggersGrid}>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Event
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              URL Contains
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Sync Settings
              <Tooltip
                title={
                  <div>
                    Choose which profiles you want to sync these events for. All
                    contacts available (recommended) OR restrict sync to just
                    audiences chosen in the integration sync above.
                  </div>
                }
              >
                <QuestionCircleOutlined />
              </Tooltip>
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Events Sent
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Actions
            </div>
            {eventTriggers.map(trigger => (
              <EventTrigger
                key={trigger.id}
                trigger={trigger}
                metrics={availableMetrics}
                eventsStats={eventsStats}
                configurationId={configurationId}
                update={update}
                remove={remove}
              />
            ))}
          </div>
        ) : null}
        {canAdd ? (
          <PrimaryButton
            className={classNames(
              'integrations__header-add-button-secondary',
              'integrations__header-add-button-action',
              'integrations__header-button-no-margin',
            )}
            onClick={() => create(configurationId, {})}
            leftIcon={<PlusOutlined />}
          >
            Event
          </PrimaryButton>
        ) : null}
      </div>
    </AccordionItem>
  );
};
